import {Component, Inject, OnInit} from '@angular/core';
import {FormGroup, UntypedFormBuilder, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {LevelsService} from "@core/services/backend/common/service/levels.service";
import {Level} from "@core/services/backend/common/interface/levels";

@Component({
    selector: 'vex-levels-create-update',
    templateUrl: './levels-create-update.component.html',
    styleUrls: ['./levels-create-update.component.scss']
})
export class LevelsCreateUpdateComponent implements OnInit {

    form: FormGroup;
    mode: 'create' | 'update' = 'create';
    availableParents: Level[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) public defaults: any,
                private dialogRef: MatDialogRef<LevelsCreateUpdateComponent>,
                private fb: UntypedFormBuilder,
                private translateService: TranslateService,
                private snackbar: MatSnackBar,
                private levels: LevelsService) {
    }

    ngOnInit() {
        // Normalizar el objeto que viene del backend (puede tener ID en mayúscula)
        if (this.defaults) {
            // Si viene ID en mayúscula, convertirlo a minúscula
            if ((this.defaults as any).ID !== undefined && this.defaults.id === undefined) {
                this.defaults.id = (this.defaults as any).ID;
            }
            // Normalizar parent_id (puede venir en diferentes formatos)
            if (this.defaults.parent_id === undefined || this.defaults.parent_id === null) {
                const parentId = (this.defaults as any).ParentID || 
                                (this.defaults as any).ParentId || 
                                (this.defaults as any).parentId || 
                                null;
                this.defaults.parent_id = parentId;
            }
        }
        
        // Verificar si hay un ID válido para determinar el modo
        const hasValidId = this.defaults && 
                          (this.defaults.id != null && this.defaults.id !== undefined && this.defaults.id !== 0);
        
        if (hasValidId) {
            this.mode = 'update';
        } else {
            this.mode = 'create';
            if (!this.defaults) {
                this.defaults = {} as Level;
            }
        }

        // Normalizar parent_id antes de crear el formulario
        let parentIdValue = null;
        if (this.defaults) {
            if (this.defaults.parent_id != null) {
                parentIdValue = Number(this.defaults.parent_id);
            } else {
                // Intentar obtener de diferentes formatos
                const parentId = (this.defaults as any).ParentID || 
                                (this.defaults as any).ParentId || 
                                (this.defaults as any).parentId;
                if (parentId != null) {
                    parentIdValue = Number(parentId);
                }
            }
        }

        this.form = this.fb.group({
            id: [this.defaults.id || null],
            code: [this.defaults.code || '', [Validators.required]],
            description: [this.defaults.description || '', [Validators.required]],
            level: [this.defaults.level != null ? this.defaults.level : 1, [Validators.required, Validators.min(1)]],
            parent_id: [parentIdValue],
            active: [this.defaults.active !== false]
        });

        // Cargar niveles disponibles para ser padres
        this.loadAvailableParents();
        
        // Recargar padres disponibles cuando cambie el nivel
        this.form.get('level')?.valueChanges.subscribe(() => {
            this.loadAvailableParents();
        });
    }

    loadAvailableParents() {
        // Cargar todos los niveles disponibles, excluyendo el actual si estamos en modo update
        this.levels.list('', 0, 10000, 'level ASC, code ASC').subscribe({
            next: (data: any) => {
                const levels = data.data || data || [];
                // Normalizar los niveles y filtrar el actual si estamos en modo update
                const normalizedLevels = levels.map((level: any) => {
                    const levelId = (level as any).ID || level.id;
                    return {
                        ...level,
                        id: levelId
                    };
                });
                
                // Obtener el nivel del nivel que se está editando
                const currentLevel = this.form.get('level')?.value || this.defaults?.level || 1;
                
                // Filtrar: solo mostrar niveles del nivel anterior (currentLevel - 1)
                // Si currentLevel es 1, mostrar solo niveles 0 (raíz)
                // Si currentLevel es 2, mostrar solo niveles 1
                // Si currentLevel es 0 o no tiene nivel, mostrar todos
                let filteredLevels = normalizedLevels;
                if (currentLevel > 0) {
                    const targetLevel = currentLevel - 1;
                    filteredLevels = normalizedLevels.filter((level: Level) => {
                        const levelValue = level.level || (level as any).Level || 0;
                        return levelValue === targetLevel;
                    });
                }
                
                // Excluir el nivel actual si estamos en modo update
                if (this.mode === 'update' && this.defaults.id) {
                    this.availableParents = filteredLevels.filter((level: Level) => {
                        return level.id !== this.defaults.id;
                    });
                } else {
                    this.availableParents = filteredLevels;
                }
                
                // Después de cargar los padres, establecer el valor del formulario si hay parent_id
                // Esto asegura que el mat-select muestre el valor correcto
                const currentParentId = this.form.get('parent_id')?.value;
                if (currentParentId != null) {
                    // Normalizar el parent_id para comparación (convertir a número si es necesario)
                    const parentIdValue = Number(currentParentId);
                    // Buscar si el parent_id existe en availableParents
                    const parentExists = this.availableParents.some(p => Number(p.id) === parentIdValue);
                    if (parentExists) {
                        // Usar setTimeout para asegurar que el mat-select esté renderizado
                        setTimeout(() => {
                            this.form.get('parent_id')?.setValue(parentIdValue, { emitEvent: false });
                        }, 0);
                    } else {
                        // Si no existe, puede ser que el padre fue eliminado o no está disponible
                        // Mantener el valor pero mostrar un warning en consola
                        console.warn('El nivel padre especificado no está disponible en la lista');
                    }
                }
            },
            error: (err) => {
                console.error('Error loading available parents:', err);
                this.availableParents = [];
            }
        });
    }

    save() {
        // Verificar el ID del formulario para determinar el modo real
        const formId = this.form.get('id')?.value;
        const hasValidId = formId != null && formId !== undefined && formId !== 0;
        
        // Si el modo es update pero no hay ID válido, mostrar error
        if (this.mode === 'update' && !hasValidId) {
            this.snackbar.open('Error: ID del nivel no válido para actualizar', '', {
                duration: 3000
            });
            return;
        }
        
        if (this.mode === 'create' || !hasValidId) {
            this.create();
        } else {
            this.update();
        }
    }

    create() {
        const level = this.form.value;
        delete level.id;
        this.levels.create(level).subscribe({
            next: (result) => {
                this.snackbar.open('Nivel creado correctamente', '', {
                    duration: 3000
                });
                this.dialogRef.close(result);
            },
            error: (err) => {
                this.snackbar.open('Error al crear el nivel', '', {
                    duration: 3000
                });
            }
        });
    }

    update() {
        const level = this.form.value;
        if (!level.id) {
            this.snackbar.open('Error: ID del nivel no válido', '', {
                duration: 3000
            });
            return;
        }
        this.levels.update(level).subscribe({
            next: (result) => {
                this.snackbar.open('Nivel actualizado correctamente', '', {
                    duration: 3000
                });
                this.dialogRef.close(result);
            },
            error: (err) => {
                this.snackbar.open('Error al actualizar el nivel', '', {
                    duration: 3000
                });
            }
        });
    }

    cancel() {
        this.dialogRef.close();
    }

    /**
     * Compara dos parent_id para el mat-select
     */
    compareParentId(id1: any, id2: any): boolean {
        if (id1 == null && id2 == null) {
            return true;
        }
        if (id1 == null || id2 == null) {
            return false;
        }
        return Number(id1) === Number(id2);
    }
}

